/**
 * Vello motion tokens (S055; IMPORT-0001 §3/§4). Verbatim from `tokens/motion.css`.
 *
 * Motion is calm: a standard ease with no bounce or spring. Hover and press use `--ease-out` (the A08 console's
 * `ease-out`, alias `--ease-hover`) over 120 ms; layers (menus, drawer, dialogs, palette, scrims) use `--ease-standard`.
 *
 * Reduced motion (D547, owner decision of the design-fidelity wave): the web follows the DESIGN — under
 * `prefers-reduced-motion: reduce` only the FX effect classes (`renderEffectsCss()`) are stilled (`transition: none` /
 * `animation: none`); hover, press, menus, drawers, dialogs, the sidebar width and the count-up keep animating. The
 * D387 rule "nothing moves" is retired for the web. The `REDUCED_MOTION_*` constants below remain only for the
 * surfaces that still still themselves — the chart entrance fade and the native (React Native) renderers.
 */
export const EASING = {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    /** Hover / press transitions of the console (A08 `ease-out`) — the same curve as `out`, named for its job. */
    hover: 'cubic-bezier(0, 0, 0.2, 1)',
};
/** Durations in milliseconds. */
export const DURATION = {
    fast: 120,
    base: 200,
    slow: 320,
    /** A navigation row's hover / press (A08 `.14s ease-out`). */
    nav: 140,
    /** A KPI card's hover lift (A08 `.16s ease-out`). */
    card: 160,
    /** The confirm/edit dialog and the command palette entrance (`.24s`). */
    dialog: 240,
    /** The photo modal entrance (`.28s`). */
    photo: 280,
    /** The active navigation pill fading in (`.36s standard`). */
    pill: 360,
    /** The click flash of a navigation row before it settles into the pill. */
    flash: 40,
    /** A toast's life: the bar drains over this long (`4s linear`). */
    toastLife: 4000,
};
/** The duration a chart or native consumer that stills itself must use when `prefers-reduced-motion` is set: none. */
export const REDUCED_MOTION_DURATION = 0;
/**
 * For the surfaces that still themselves under `prefers-reduced-motion: reduce` (the chart entrance fade, the native
 * renderers — NOT the web shell, see the file header): every movement, colour change and loop is instant
 * ({@link REDUCED_MOTION_DURATION}); the ONLY motion left is an opacity cross-fade, and it lasts at most this long
 * (owner D381 #6, W5-THEME-TARGETS D387). A fade this short is not motion the setting asks to remove, and it keeps a
 * layer that appears or disappears from flashing.
 */
export const REDUCED_MOTION_FADE_MAX_MS = DURATION.fast;
/**
 * The step between two neighbours of a list that enter one after the other (chart bars, ring segments): a short cascade (FX-07: 60ms, at most six items),
 * never a wait. A consumer multiplies it by the item index and caps the index so a long series stays quick (D507).
 */
export const STAGGER_STEP_MS = 60;
/** The most siblings a cascade staggers (A08 FX-07: delays at 60ms steps up to the 6th); later ones arrive with the 6th. */
export const STAGGER_MAX_ITEMS = 6;
//# sourceMappingURL=motion.js.map